/**
 * The classic cabinet's play-access gate (Monetization v2, sc-6940).
 *
 * Mirror of apps/shell/src/cabinet/play-access.ts — the '84 cabinet must
 * gate INSERT COIN the same way the remaster does. Every launch routes
 * through resolvePlayAccess() BEFORE the cartridge mounts. The five
 * branches:
 *
 *   entitled (active pass)          → 'play'         (no token spent)
 *   signed-in + tokens remaining    → 'play'         (one token spent)
 *   signed-in + exhausted           → 'paywall'      (stub until sc-6944)
 *   anon + free taste unused        → 'play'         (device-metered)
 *   anon + free taste used          → 'login'        (the wall)
 *
 * The server taste is authoritative and atomic (sc-6940). The signed-in
 * token is NOT spent here — the gate only CHECKS eligibility; the token
 * is spent at run START (launcher first round, sc-7036) so a game that
 * fails to load costs the player nothing. If the wallet/taste call
 * throws, we do NOT hand out a free run: signed-in → paywall/retry,
 * anon → login wall.
 */

import { consumeTaste, getWallet } from '../api/client.js';
import { isSignedIn } from '@arcraz/arcadia-auth-web';

export type PlayVerdict =
  | { grant: 'play'; access: 'pass' | 'token' | 'free-taste' }
  | { grant: 'login' }
  | { grant: 'paywall'; resetsAtUtc?: string }
  | { grant: 'error' };

export async function resolvePlayAccess(): Promise<PlayVerdict> {
  if (!isSignedIn()) {
    // Anonymous: consume the daily free taste ATOMICALLY server-side
    // (sc-6940). Reading a client-cached meter raced — two rapid
    // launches both played free. The server is the single authority.
    // A throw (503 fail-closed / network) → login wall, never a free run.
    try {
      const taste = await consumeTaste();
      return taste.allowed
        ? { grant: 'play', access: 'free-taste' }
        : { grant: 'login' };
    } catch {
      return { grant: 'login' };
    }
  }

  // Signed in: the wallet CHECKS eligibility — a pass is unlimited, else
  // the player must have tokens left. The token is NOT spent here; it's
  // spent at run start (sc-7036) so a failed game load costs nothing.
  try {
    const wallet = await getWallet();
    if (wallet.entitlement) {
      return { grant: 'play', access: 'pass' };
    }
    if (wallet.tokensRemaining > 0) {
      return { grant: 'play', access: 'token' };
    }
    // No tokens and no pass ⇒ exhausted.
    return { grant: 'paywall', resetsAtUtc: wallet.resetsAtUtc };
  } catch {
    // Wallet read threw (incl. the 503 fail-closed path). Never a free
    // run for a signed-in player — send them to the paywall/retry.
    return { grant: 'error' };
  }
}
